/**
 * Daily report aggregation.
 *
 * Pure functions over the day's leads and deliveries. Two audiences:
 *
 *  - The PARTNER sees only leads actually forwarded to them, and only fields
 *    that are theirs to see. It must never leak another partner's volume, our
 *    spam rates, or which sites underperform.
 *  - INTERNAL sees everything, including what went wrong, so the report doubles
 *    as a daily health check on the pipeline itself.
 */
import type { DeliveryRecord, Destination, LeadRecord, SiteDefinition } from "../types.ts";

export interface LeadLine {
  id: string;
  submittedAt: string;
  siteKey: string;
  siteName: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  location: string;
  interest: string;
  /** Qualifying answers flattened for tabular display, e.g. "Looking to: Leasa". */
  details: string;
  message: string;
  /** Delivery state for this audience, e.g. "delivered", "sent", "retrying". */
  status: string;
}

export interface SiteBreakdown {
  siteKey: string;
  siteName: string;
  total: number;
}

export interface PartnerReport {
  dateKey: string;
  destinationKey: string;
  destinationName: string;
  totalForwarded: number;
  /** Confirmed accepted by the recipient's mail server (via provider webhook). */
  delivered: number;
  /** Handed to the mail provider; no delivery confirmation received (yet). */
  sent: number;
  /** Still queued or being retried. */
  inFlight: number;
  /** Bounced, marked as spam, or gave up. */
  failed: number;
  bySite: SiteBreakdown[];
  leads: LeadLine[];
  medianMinutesToForward: number | null;
}

export interface DestinationBreakdown {
  key: string;
  name: string;
  forwarded: number;
  delivered: number;
  bounced: number;
  dead: number;
  pending: number;
}

export interface InternalReport {
  dateKey: string;
  totalLeads: number;
  clean: number;
  suspect: number;
  spam: number;
  duplicates: number;
  heldForReview: number;
  skipped: number;
  bySite: Array<SiteBreakdown & { clean: number; suspect: number; spam: number; duplicates: number }>;
  byDestination: DestinationBreakdown[];
  deadDeliveries: number;
  pendingDeliveries: number;
  medianMinutesToForward: number | null;
  /** Plain-English list of things a human should look at today. */
  problems: string[];
}

// ---------------------------------------------------------------------------

function siteNameFor(siteKey: string, sites: readonly SiteDefinition[]): string {
  return sites.find((site) => site.key === siteKey)?.name ?? siteKey;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? null;
  const low = sorted[middle - 1] ?? 0;
  const high = sorted[middle] ?? 0;
  return (low + high) / 2;
}

/** Minutes between submission and the moment we handed it to the mail provider. */
function minutesToForward(lead: LeadRecord, delivery: DeliveryRecord): number | null {
  if (!delivery.sentAt) return null;
  const submitted = Date.parse(lead.submittedAt);
  const sent = Date.parse(delivery.sentAt);
  if (Number.isNaN(submitted) || Number.isNaN(sent)) return null;
  const diff = (sent - submitted) / 60_000;
  return diff >= 0 ? Math.round(diff * 10) / 10 : null;
}

function location(lead: LeadRecord): string {
  return [lead.city, lead.state, lead.postalCode].filter(Boolean).join(", ");
}

/** How a delivery reads to a partner: they don't need our retry vocabulary. */
function partnerStatus(status: DeliveryRecord["status"]): string {
  switch (status) {
    case "delivered":
      return "delivered";
    case "sent":
      return "sent";
    case "bounced":
      return "bounced";
    case "complained":
      return "marked as spam";
    case "dead":
      return "failed";
    default:
      return "in progress";
  }
}

// ---------------------------------------------------------------------------

export function buildPartnerReport(input: {
  dateKey: string;
  destination: Destination;
  leads: readonly LeadRecord[];
  deliveries: readonly DeliveryRecord[];
  sites: readonly SiteDefinition[];
}): PartnerReport {
  const { dateKey, destination, leads, deliveries, sites } = input;

  const leadsById = new Map(leads.map((lead) => [lead.id, lead]));
  const mine = deliveries.filter((delivery) => delivery.destination === destination.id);

  const lines: LeadLine[] = [];
  const forwardTimes: number[] = [];
  let delivered = 0;
  let sent = 0;
  let inFlight = 0;
  let failed = 0;

  for (const delivery of mine) {
    const lead = leadsById.get(delivery.lead);
    if (!lead) continue;

    switch (delivery.status) {
      case "delivered":
        delivered += 1;
        break;
      case "sent":
        sent += 1;
        break;
      case "bounced":
      case "complained":
      case "dead":
        failed += 1;
        break;
      case "pending":
      case "sending":
        inFlight += 1;
        break;
    }

    const elapsed = minutesToForward(lead, delivery);
    if (elapsed !== null) forwardTimes.push(elapsed);

    lines.push({
      id: lead.id,
      submittedAt: lead.submittedAt,
      siteKey: lead.siteKey,
      siteName: siteNameFor(lead.siteKey, sites),
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      location: location(lead),
      interest: lead.interest,
      details: lead.details.map((d) => `${d.label}: ${d.value}`).join("; "),
      message: lead.message,
      status: partnerStatus(delivery.status),
    });
  }

  lines.sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));

  const bySiteMap = new Map<string, SiteBreakdown>();
  for (const line of lines) {
    const entry = bySiteMap.get(line.siteKey) ?? {
      siteKey: line.siteKey,
      siteName: line.siteName,
      total: 0,
    };
    entry.total += 1;
    bySiteMap.set(line.siteKey, entry);
  }

  return {
    dateKey,
    destinationKey: destination.key,
    destinationName: destination.name,
    totalForwarded: lines.length,
    delivered,
    sent,
    inFlight,
    failed,
    bySite: [...bySiteMap.values()].sort((a, b) => b.total - a.total),
    leads: lines,
    medianMinutesToForward: median(forwardTimes),
  };
}

export function buildInternalReport(input: {
  dateKey: string;
  leads: readonly LeadRecord[];
  deliveries: readonly DeliveryRecord[];
  destinations: readonly Destination[];
  sites: readonly SiteDefinition[];
}): InternalReport {
  const { dateKey, leads, deliveries, destinations, sites } = input;
  const leadsById = new Map(leads.map((lead) => [lead.id, lead]));

  let clean = 0;
  let suspect = 0;
  let spam = 0;
  let duplicates = 0;
  let heldForReview = 0;
  let skipped = 0;

  const bySiteMap = new Map<string, InternalReport["bySite"][number]>();

  for (const lead of leads) {
    if (lead.isDuplicate) duplicates += 1;
    if (lead.quality === "clean") clean += 1;
    if (lead.quality === "suspect") suspect += 1;
    if (lead.quality === "spam") spam += 1;
    if (lead.routingStatus === "held") heldForReview += 1;
    if (lead.routingStatus === "skipped") skipped += 1;

    const entry = bySiteMap.get(lead.siteKey) ?? {
      siteKey: lead.siteKey,
      siteName: siteNameFor(lead.siteKey, sites),
      total: 0,
      clean: 0,
      suspect: 0,
      spam: 0,
      duplicates: 0,
    };
    entry.total += 1;
    if (lead.quality === "clean") entry.clean += 1;
    if (lead.quality === "suspect") entry.suspect += 1;
    if (lead.quality === "spam") entry.spam += 1;
    if (lead.isDuplicate) entry.duplicates += 1;
    bySiteMap.set(lead.siteKey, entry);
  }

  const byDestination: DestinationBreakdown[] = [];
  const forwardTimes: number[] = [];
  let deadDeliveries = 0;
  let pendingDeliveries = 0;

  for (const destination of destinations) {
    const mine = deliveries.filter((delivery) => delivery.destination === destination.id);
    const breakdown: DestinationBreakdown = {
      key: destination.key,
      name: destination.name,
      forwarded: mine.length,
      delivered: 0,
      bounced: 0,
      dead: 0,
      pending: 0,
    };

    for (const delivery of mine) {
      if (delivery.status === "delivered") breakdown.delivered += 1;
      if (delivery.status === "bounced" || delivery.status === "complained") breakdown.bounced += 1;
      if (delivery.status === "dead") breakdown.dead += 1;
      if (delivery.status === "pending" || delivery.status === "sending") breakdown.pending += 1;

      const lead = leadsById.get(delivery.lead);
      if (lead) {
        const elapsed = minutesToForward(lead, delivery);
        if (elapsed !== null) forwardTimes.push(elapsed);
      }
    }

    deadDeliveries += breakdown.dead;
    pendingDeliveries += breakdown.pending;
    if (breakdown.forwarded > 0 || destination.active) byDestination.push(breakdown);
  }

  // --- things a human should look at ---------------------------------------
  const problems: string[] = [];

  if (deadDeliveries > 0) {
    problems.push(
      `${deadDeliveries} delivery(ies) gave up permanently and need attention — these leads never reached the partner.`,
    );
  }
  const bounced = byDestination.reduce((sum, entry) => sum + entry.bounced, 0);
  if (bounced > 0) {
    problems.push(`${bounced} email(s) bounced or were marked as spam — check the recipient addresses.`);
  }
  if (pendingDeliveries > 0) {
    problems.push(`${pendingDeliveries} delivery(ies) were still in flight when this report was generated.`);
  }
  if (heldForReview > 0) {
    problems.push(
      `${heldForReview} lead(s) were held for review and NOT forwarded. Release them if they look genuine.`,
    );
  }
  const unrouted = leads.filter((lead) => lead.routingStatus === "pending").length;
  if (unrouted > 0) {
    problems.push(`${unrouted} lead(s) are still waiting to be routed.`);
  }
  if (leads.length > 0 && spam / leads.length > 0.5) {
    problems.push(
      `${Math.round((spam / leads.length) * 100)}% of today's submissions were classified as spam — worth checking the forms for bot traffic.`,
    );
  }

  return {
    dateKey,
    totalLeads: leads.length,
    clean,
    suspect,
    spam,
    duplicates,
    heldForReview,
    skipped,
    bySite: [...bySiteMap.values()].sort((a, b) => b.total - a.total),
    byDestination,
    deadDeliveries,
    pendingDeliveries,
    medianMinutesToForward: median(forwardTimes),
    problems,
  };
}
